// Statistik-Bildschirm, IMG_4884 (DEUTSCHLAND), 4885 bis 4887 (EUROPA),
// 4888 (WEITERE), 01.09.2026. Rechnet clientseitig aus Bundle + eigenen
// Eintraegen, kein eigener Endpunkt (siehe lib/statistik.ts).

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { RingZeile } from "../bausteine/RingZeile.js";
import { SammlungsWahl } from "../bausteine/SammlungsWahl.js";
import { Unterseite } from "../bausteine/Unterseite.js";
import { useBundle } from "../lib/bundle.js";
import { persoenlicheSammlung, useEntries } from "../lib/entries.js";
import { aktiveSammlung } from "../lib/collections.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import {
  bereichSchluessel,
  bundeslandRinge,
  laenderRinge,
  reiterKennzeichen,
  sonderRinge,
  type Bereich,
  type StatistikReiter,
} from "../lib/statistik.js";
import { LAENDERKENNZEICHEN_MODUS, fortschrittFuer } from "../lib/treffer.js";

const REITER = ["Deutschland", "Europa", "Weitere"] as const;
type Reiter = (typeof REITER)[number];

const REITER_ZU_SCHLUESSEL: Record<Reiter, StatistikReiter> = {
  Deutschland: "deutschland",
  Europa: "europa",
  Weitere: "weitere",
};

/**
 * Jede Zeile fuehrt auf ihre Unterseite mit den einzelnen Kennzeichen
 * (docs/reference/statistik-detail-*.png, 08.09.2026). Der Bundeslandname
 * steckt im Schluessel und wird erst hier fuer die Adresszeile kodiert,
 * siehe bereichSchluessel().
 */
function ZeileMitLink({
  bereich,
  name,
  gesehen,
  gesamt,
  sammlungId,
}: {
  bereich: Bereich;
  name: string;
  gesehen: number;
  gesamt: number;
  /** Die Unterseite ist eine eigene Adresse und kennt die Auswahl hier sonst nicht. */
  sammlungId: number | undefined;
}) {
  const ziel = `/statistik/${encodeURIComponent(bereichSchluessel(bereich))}`;
  return (
    <Link
      to={sammlungId === undefined ? ziel : `${ziel}?sammlung=${sammlungId}`}
      className="block"
    >
      <RingZeile name={name} gesehen={gesehen} gesamt={gesamt} />
    </Link>
  );
}

/**
 * Die Zeile ganz oben, wenn eine gemeinsame Sammlung gewaehlt ist: was in
 * der eigenen Sammlung liegt und in dieser fehlt. Kein Ring, weil es kein
 * Verhaeltnis zu einer Gesamtzahl ist, sondern eine Restmenge.
 */
function FehltZeile({
  anzahl,
  zielName,
  reiter,
  sammlungId,
}: {
  anzahl: number;
  zielName: string;
  reiter: StatistikReiter;
  sammlungId: number;
}) {
  return (
    <Link
      to={`/statistik/fehlend-${reiter}?sammlung=${sammlungId}`}
      className="flex items-center gap-4 border-t border-neutral-100 px-4 py-3 first:border-t-0"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-punkte-track text-sm font-medium">
        {anzahl}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base">Nur in der eigenen Sammlung</div>
        <div className="truncate text-sm text-neutral-500">
          {anzahl === 1
            ? "1 Kennzeichen fehlt"
            : `${anzahl} Kennzeichen fehlen`}{" "}
          in {zielName}
        </div>
      </div>
      <ChevronRight size={20} className="shrink-0 text-neutral-400" />
    </Link>
  );
}

export function Statistik({
  nutzer,
  sammlungen,
}: {
  nutzer: Nutzer;
  sammlungen: Sammlung[];
}) {
  const [reiter, setReiter] = useState<Reiter>("Deutschland");
  const bundle = useBundle();
  // Die aktive Sammlung, nicht die persoenliche. Wer in eine gemeinsame
  // Sammlung umschaltet und dort eintraegt, will sie auch hier zaehlen
  // sehen; vorher zeigte die Statistik stur die eigene und meldete null,
  // waehrend der Hauptscreen daneben die Funde anzeigte.
  const activeCollectionId = (
    nutzer.settings as { activeCollectionId?: number } | undefined
  )?.activeCollectionId;
  const aktive = aktiveSammlung(sammlungen, activeCollectionId);
  // Nur die Ansicht, nicht die aktive Sammlung: siehe SammlungsWahl.tsx.
  // Die Wahl steht in der Adresse, nicht im Zustand: die Unterseiten sind
  // eigene Adressen, und wer von dort zurueckkommt, soll nicht wieder auf
  // der aktiven Sammlung landen.
  const [suche, setSuche] = useSearchParams();
  const gewaehlteId = Number(suche.get("sammlung"));
  const sammlung = sammlungen.find((s) => s.id === gewaehlteId) ?? aktive;
  const eintraege = useEntries(sammlung?.id);
  const persoenliche = persoenlicheSammlung(sammlungen);
  // Zweite Abfrage nur, wenn eine gemeinsame Sammlung angesehen wird. Ist
  // die eigene gewaehlt, teilen sich beide Aufrufe den Query-Key und es
  // geht keine Anfrage mehr raus.
  const eigene = useEntries(persoenliche?.id);

  const laender = bundle.data?.laender ?? [];
  const kennzeichen = bundle.data?.kennzeichen ?? [];
  const karte = eintraege.data ?? new Map<number, number>();
  const eigeneKarte = eigene.data ?? new Map<number, number>();

  // Was in der eigenen Sammlung liegt und in der angesehenen fehlt, auf den
  // Reiter beschraenkt: der Knopf zum Uebertragen steht unter diesem Reiter
  // und darf nicht heimlich die Funde der anderen mitnehmen.
  const fehlend =
    sammlung && persoenliche && sammlung.id !== persoenliche.id
      ? reiterKennzeichen(
          REITER_ZU_SCHLUESSEL[reiter],
          kennzeichen,
          laender,
        ).filter((k) => eigeneKarte.has(k.id) && !karte.has(k.id))
      : [];

  const deutschlandId = laender.find((l) => l.iso2 === "DE")?.id;
  const deutschland = fortschrittFuer("DE", kennzeichen, laender, karte);
  const laenderkennzeichen = fortschrittFuer(
    LAENDERKENNZEICHEN_MODUS,
    kennzeichen,
    laender,
    karte,
  );

  return (
    <Unterseite
      titel="Statistik"
      kinder={
        <>
          <SammlungsWahl
            sammlungen={sammlungen}
            gewaehlteId={sammlung?.id}
            onWaehlen={(id) =>
              setSuche({ sammlung: String(id) }, { replace: true })
            }
          />

          <div className="mb-2 flex rounded bg-neutral-100 p-1 text-sm font-medium">
            {REITER.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReiter(r)}
                className={`flex-1 rounded py-2 uppercase ${
                  reiter === r
                    ? "bg-flaeche-inhalt text-akzent shadow-sm"
                    : "text-neutral-500"
                }`}
              >
                {r}
              </button>
            ))}
          </div>

          <div className="rounded bg-flaeche-inhalt shadow-sm">
            {fehlend.length > 0 && sammlung && (
              <FehltZeile
                anzahl={fehlend.length}
                zielName={sammlung.name}
                reiter={REITER_ZU_SCHLUESSEL[reiter]}
                sammlungId={sammlung.id}
              />
            )}

            {reiter === "Deutschland" && (
              <>
                <ZeileMitLink
                  bereich={{ art: "land", iso2: "DE" }}
                  name="Deutschland"
                  gesehen={deutschland.gesehen}
                  gesamt={deutschland.gesamt}
                  sammlungId={sammlung?.id}
                />
                {/* Bundesweit und Diplomatenkennzeichen stehen im Original
                    zwischen Deutschland und den Bundeslaendern (docs/reference/
                    statistik-deutschland-2026-09-08.png). Sie haben kein
                    Bundesland und fehlten deshalb ganz. */}
                {sonderRinge(kennzeichen, laender, karte).map((z) => (
                  <ZeileMitLink key={z.name} {...z} sammlungId={sammlung?.id} />
                ))}
                {bundeslandRinge(kennzeichen, karte, deutschlandId).map((z) => (
                  <ZeileMitLink key={z.name} {...z} sammlungId={sammlung?.id} />
                ))}
              </>
            )}

            {reiter === "Europa" && (
              <>
                <ZeileMitLink
                  bereich={{ art: "laenderkennzeichen" }}
                  name="Länderkennzeichen"
                  gesehen={laenderkennzeichen.gesehen}
                  gesamt={laenderkennzeichen.gesamt}
                  sammlungId={sammlung?.id}
                />
                {laenderRinge(kennzeichen, laender, karte, "europa").map(
                  (z) => (
                    <ZeileMitLink
                      key={z.name}
                      {...z}
                      sammlungId={sammlung?.id}
                    />
                  ),
                )}
              </>
            )}

            {reiter === "Weitere" &&
              laenderRinge(kennzeichen, laender, karte, "andere").map((z) => (
                <ZeileMitLink key={z.name} {...z} sammlungId={sammlung?.id} />
              ))}
          </div>
        </>
      }
    />
  );
}
